import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames/bind'
import PropTypes from 'prop-types'

import Icon from '@ui-components/icon'
import colors from '@ui-components/colors'
import ButtonIcon from '@ui-components/buttonicon'
import Tooltip from '@ui-components/tooltip'
import styles from './TextInput.module.scss'
import debounce from 'utils/debounce'
import { DEVICE_SIZE } from 'utils/constants'
import useMediaQuery from 'hooks/useMediaQuery'

const cx = classNames.bind(styles)

const TextInput = ({
  autoComplete,
  classes,
  disabled,
  invalidMessage,
  isValid,
  label,
  maxLength,
  name,
  onBlur,
  onChange,
  onFocus,
  pattern,
  placeholder,
  showLoader,
  showPasswordLabel,
  tooltipContent,
  type,
  value,
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [stateType, setStateType] = useState(type)
  const [openInfoTooltip, setOpenInfoTooltip] = useState(false)
  const infoRef = useRef(null)
  const inputRef = useRef('')
  const isTablet = useMediaQuery(`(min-width: ${DEVICE_SIZE.TABLET})`)

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.value = value
    }
  }, [value])

  const handleChange = ({ target: { value } }) => {
    onChange(value)
  }

  const debouncedOnChange = debounce(handleChange, 950)

  const handleBlur = (event) => {
    if (onBlur) {
      onBlur(event.target.value)
    } else {
      onChange(event.target.value)
    }
    if (onFocus) {
      const isFocused = false
      onFocus(isFocused)
    }
  }

  const handleFocus = () => {
    if (onFocus) {
      const isFocused = true
      onFocus(isFocused)
    }
  }

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
    setStateType(stateType === 'password' ? 'text' : 'password')
  }

  const handleOpenInfoTooltip = () => {
    setOpenInfoTooltip(!openInfoTooltip)
  }

  let className = ` ${styles.field}`
  className +=
    stateType === 'password' || showPassword ? ` ${styles.password}` : ''
  className +=
    isValid !== null && !isValid && invalidMessage ? ` ${styles.invalid}` : ''
  className += isValid ? ` ${styles.success}` : ''
  className += disabled ? ` ${styles.disabled}` : ''
  className += ` ${cx(classes)}`

  return (
    <div className={className}>
      {label && (
        <label className={styles.label} htmlFor={name}>
          {label}
        </label>
      )}
      <div className={styles.wrapper}>
        <input
          autoComplete={
            stateType.toLocaleLowerCase() === 'password'
              ? 'new-password'
              : autoComplete
          }
          className={styles.input}
          disabled={disabled}
          id={name}
          maxLength={maxLength}
          name={`${name} ${Math.random()}`}
          onBlur={handleBlur}
          onChange={debouncedOnChange}
          onFocus={handleFocus}
          pattern={pattern}
          placeholder={placeholder}
          ref={inputRef}
          type={stateType.toLocaleLowerCase()}
        />
        {(showLoader ||
          stateType === 'password' ||
          showPassword ||
          tooltipContent) && (
          <div className={styles.loader}>
            {showLoader && <span className={styles.spinnerForm} />}
            {(stateType === 'password' || showPassword) && (
              <div className={styles.showPasswordToggle}>
                <label htmlFor={`${name}_show`}>
                  <input
                    checked={showPassword}
                    disabled={disabled}
                    id={`${name}_show`}
                    name='showPassword'
                    onChange={handleShowPassword}
                    type='checkbox'
                  />
                  <span>{showPasswordLabel}</span>
                </label>
              </div>
            )}
            {tooltipContent && (
              <>
                <ButtonIcon
                  className={styles.infoButtonIcon}
                  dataTestId='license-info-tooltip'
                  icon={<Icon fill={colors.BLACK} icon='info' />}
                  onClick={() => handleOpenInfoTooltip()}
                  reference={infoRef}
                />

                <Tooltip
                  coords={infoRef?.current?.getBoundingClientRect()}
                  id={'license-info-content'}
                  onClose={() => handleOpenInfoTooltip()}
                  open={openInfoTooltip}
                  position={isTablet ? 'bottom' : 'left'}
                >
                  <p className={styles.tooltipContent}>{tooltipContent}</p>
                </Tooltip>
              </>
            )}
          </div>
        )}
      </div>
      {isValid !== null && !isValid && invalidMessage && (
        <span className={styles.invalidMessage}>{invalidMessage}</span>
      )}
    </div>
  )
}

TextInput.defaultProps = {
  autoComplete: 'off',
  classes: {},
  disabled: false,
  invalidMessage: '',
  isValid: null,
  label: null,
  maxLength: null,
  onBlur: null,
  onChange: null,
  onFocus: null,
  pattern: '',
  placeholder: '',
  showLoader: null,
  showPasswordLabel: null,
  tooltipContent: null,
  type: 'text',
  value: '',
}

TextInput.propTypes = {
  autoComplete: PropTypes.string,
  classes: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  invalidMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  isValid: PropTypes.bool,
  label: PropTypes.string,
  maxLength: PropTypes.number,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  showLoader: PropTypes.bool,
  showPasswordLabel: PropTypes.string,
  tooltipContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  type: PropTypes.string,
  value: PropTypes.string,
}

export default TextInput
